import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, KeyRound, ArrowLeft, CheckCircle2, ShieldCheck } from "lucide-react";

/**
 * Página "Esqueci minha senha"
 * 
 * Fluxo em 2 etapas:
 * 1. Usuário informa email → sistema envia código de 6 dígitos
 * 2. Usuário insere código + nova senha → sistema valida e atualiza
 * 
 * Segurança:
 * - Anti-enumeração: mensagem genérica sempre
 * - Código com hash bcrypt no banco
 * - Expiração de 15 minutos
 * - Rate limit: 1 solicitação a cada 2 minutos por email
 * - Uso único: código invalidado após uso
 */
export default function ForgotPassword() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<"email" | "code" | "success">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [countdown, setCountdown] = useState(0);

  // Branding dinâmico
  const [branding, setBranding] = useState({
    primaryColor: "#002F59",
    accentColor: "#C0FF00",
    logoUrl: null as string | null,
    productName: "Insight NR1",
  });

  // Buscar branding quando email é preenchido e muda de step
  const brandingQuery = trpc.auth.getUserBranding.useQuery(
    { email },
    { enabled: step === "code" && email.length > 0 }
  );

  useEffect(() => {
    if (brandingQuery.data) {
      setBranding(brandingQuery.data);
    }
  }, [brandingQuery.data]);

  // Countdown para reenvio
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const requestResetMutation = trpc.auth.requestPasswordReset.useMutation({
    onSuccess: (data) => {
      toast.success(data.message || "Se existir uma conta com este e-mail, enviaremos um código.");
      setStep("code");
      setCountdown(120); // 2 minutos para reenvio
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao processar solicitação");
    },
  });

  const confirmResetMutation = trpc.auth.confirmPasswordReset.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      setStep("success");
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao redefinir senha");
    },
  });

  const handleRequestCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Por favor, informe seu email");
      return;
    }
    requestResetMutation.mutate({ email });
  };

  const handleResendCode = () => {
    if (countdown > 0) return;
    requestResetMutation.mutate({ email });
  };

  const handleConfirmReset = (e: React.FormEvent) => {
    e.preventDefault();

    if (!code || code.length !== 6) {
      toast.error("Por favor, informe o código de 6 dígitos");
      return;
    }

    if (!newPassword || newPassword.length < 8) {
      toast.error("A senha deve ter no mínimo 8 caracteres");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    confirmResetMutation.mutate({ email, code, newPassword, confirmPassword });
  };

  // Helpers de contraste
  const getContrastText = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  const bgColor = branding.primaryColor;
  const textColor = getContrastText(bgColor);
  const accentColor = branding.accentColor;
  const accentTextColor = getContrastText(accentColor);

  // ==================== TELA DE SUCESSO ====================
  if (step === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg p-8 shadow-xl text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Senha Redefinida!</h1>
            <p className="text-gray-600 mb-6">
              Sua senha foi atualizada com sucesso. Você já pode fazer login com a nova senha.
            </p>
            <Button
              onClick={() => setLocation("/")}
              className="w-full text-white font-semibold"
              style={{ backgroundColor: bgColor }}
            >
              Ir para Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== FORMULÁRIO PRINCIPAL ====================
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
      <div className="w-full max-w-md">
        {/* Logo e Título */}
        <div className="text-center mb-8">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.productName}
              className="h-12 mx-auto mb-4 object-contain"
            />
          ) : (
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full font-bold text-2xl mb-4"
              style={{ backgroundColor: accentColor, color: accentTextColor }}
            >
              <ShieldCheck className="w-8 h-8" />
            </div>
          )}
          <h1 className="text-3xl font-bold mb-2" style={{ color: textColor }}>
            {step === "email" ? "Esqueci minha senha" : "Redefinir Senha"}
          </h1>
          <p style={{ color: textColor, opacity: 0.8 }}>
            {step === "email"
              ? "Informe seu email para receber o código de verificação"
              : `Insira o código enviado para ${email}`}
          </p>
        </div>

        {/* Card do Formulário */}
        <div className="bg-white rounded-lg p-8 shadow-xl">
          {step === "email" ? (
            // ========== ETAPA 1: Solicitar Email ==========
            <form onSubmit={handleRequestCode} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-gray-300"
                    required
                    autoFocus
                    disabled={requestResetMutation.isPending}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full font-semibold"
                style={{ backgroundColor: accentColor, color: accentTextColor }}
                disabled={requestResetMutation.isPending}
              >
                {requestResetMutation.isPending ? "Enviando..." : "Enviar Código"}
              </Button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setLocation("/")}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Voltar para login
                </button>
              </div>
            </form>
          ) : (
            // ========== ETAPA 2: Código + Nova Senha ==========
            <form onSubmit={handleConfirmReset} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-gray-700 font-medium">
                  Código de Verificação
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="pl-10 text-center text-2xl tracking-widest border-gray-300 font-mono"
                    required
                    autoFocus
                    disabled={confirmResetMutation.isPending}
                    maxLength={6}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Código de 6 dígitos enviado para {email}
                  </p>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={countdown > 0 || requestResetMutation.isPending}
                    className="text-xs font-medium transition-colors disabled:opacity-50"
                    style={{ color: countdown > 0 ? "#999" : branding.primaryColor }}
                  >
                    {countdown > 0
                      ? `Reenviar em ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, "0")}`
                      : "Reenviar código"}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-gray-700 font-medium">
                  Nova Senha
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="border-gray-300"
                  required
                  disabled={confirmResetMutation.isPending}
                  minLength={8}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">
                  Confirmar Senha
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Digite a senha novamente"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="border-gray-300"
                  required
                  disabled={confirmResetMutation.isPending}
                  minLength={8}
                />
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500">As senhas não coincidem</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full font-semibold"
                style={{ backgroundColor: accentColor, color: accentTextColor }}
                disabled={confirmResetMutation.isPending || code.length !== 6 || newPassword.length < 8 || newPassword !== confirmPassword}
              >
                {confirmResetMutation.isPending ? "Redefinindo..." : "Redefinir Senha"}
              </Button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
                  disabled={confirmResetMutation.isPending}
                >
                  <ArrowLeft className="w-3 h-3" />
                  Voltar
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Rodapé */}
        <div className="text-center mt-6 text-sm" style={{ color: textColor, opacity: 0.6 }}>
          {branding.productName} - Gestão de Risco Ocupacional
        </div>
      </div>
    </div>
  );
}
